import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms'
import { Actions, ofType } from '@ngrx/effects'
import { Store } from '@ngrx/store'
import { Observable, combineLatest, of } from 'rxjs'
import {
  catchError,
  distinctUntilChanged,
  filter,
  map,
  take,
  timeout
} from 'rxjs/operators'
import { untilDestroyed } from '@ngneat/until-destroy'
import { AppState } from 'src/app/core/core.state'
import { NotificationService } from 'src/app/core/notification.service'
import { AccessRights } from 'src/app/features/role/user-role/user-role'
import { ConfigOptionsKeys } from 'src/app/shared/constants'
import { environment } from 'src/environments/environment'
import { ConfigurationOption } from './configuration-option'
import {
  MemberFormConfigValueMap,
  RegistrationCategoryFilter,
  RegistrationCategoryOptions
} from './configuration-option'
import { configurationOptionActions } from './configuration-option.actions'
import { configurationOptionSelectors } from './configuration-option.selectors'
import { ConfigurationOptionService } from './configuration-option.service'

interface ConfigurationOptionSavePayload {
  key: string
  valueToSave: string
}
const memberCategoryUrlKeys = new Set<string>([
  ConfigOptionsKeys.NL_MEMBER_CATEGORIES_PAGE_URL,
  ConfigOptionsKeys.EN_MEMBER_CATEGORIES_PAGE_URL,
  ConfigOptionsKeys.FR_MEMBER_CATEGORIES_PAGE_URL,
  ConfigOptionsKeys.DE_MEMBER_CATEGORIES_PAGE_URL
])

type MemberCategoryUrlKey =
  | ConfigOptionsKeys.NL_MEMBER_CATEGORIES_PAGE_URL
  | ConfigOptionsKeys.EN_MEMBER_CATEGORIES_PAGE_URL
  | ConfigOptionsKeys.FR_MEMBER_CATEGORIES_PAGE_URL
  | ConfigOptionsKeys.DE_MEMBER_CATEGORIES_PAGE_URL

type MemberCategoryUrlValues = Partial<Record<MemberCategoryUrlKey, string | null>>
type MemberConfigOptionsMap = Record<string, string | null>
type MemberFormEditorValue = MemberFormConfigValueMap & MemberCategoryUrlValues

export abstract class ConfigurationOptionMemberFormConfigTabBase {
  public optionValue$: Observable<MemberFormConfigValueMap>
  public configOption$: Observable<ConfigurationOption | undefined>
  public configFormValues$: Observable<MemberConfigOptionsMap>
  public valuesForm$: Observable<MemberFormEditorValue>
  public regCategoryFilters$: Observable<RegistrationCategoryFilter>
  public regCategoryOptions$: Observable<RegistrationCategoryOptions>
  public noticationAgreements$: Observable<boolean>
  public showOffersAgreements$: Observable<boolean>
  public showNewslettersAgreements$: Observable<boolean>
  public isCollapsed = false
  public isCollapsedCategory = false
  public isCollapsedRegCategoryFilter = false
  public isCollapsedMemberUrl = true
  public form: UntypedFormGroup
  public form2: UntypedFormGroup
  public registrationCategoryFilter: UntypedFormGroup
  public accessRights = AccessRights
  protected readonly memberCategoryUrlKeys = memberCategoryUrlKeys
  public showUploadDocuments =
    environment.moduleConfigs.configOptionModule.uploadDocuments
  public showProfilePicture =
    environment.moduleConfigs.configOptionModule.uploadProfilePicture
  public availableLanguages = environment.frontendLanguages.reduce(
    (acc, curr) => {
      if (curr != 'xx') return ((acc[curr] = true), acc)
      return acc
    },
    {}
  )

  constructor(
    protected store: Store<AppState>,
    protected fb: UntypedFormBuilder,
    protected notificationService: NotificationService,
    protected actions$: Actions,
    protected configOptionService: ConfigurationOptionService
  ) {}

  protected isMemberCategoryUrlKey(key: string): boolean {
    return this.memberCategoryUrlKeys.has(key)
  }

  protected initializeMemberFormConfigEditor(
    configOption$: Observable<ConfigurationOption | undefined>,
    includeMemberCategoryUrls: boolean
  ) {
    this.configFormValues$ = this.store
      .select(configurationOptionSelectors.selectAll)
      .pipe(
        distinctUntilChanged((a, b) => JSON.stringify(a) == JSON.stringify(b)),
        map((configOptions) =>
          this.configOptionService.mapEntitiesToObject(configOptions)
        )
      )

    this.configOption$ = configOption$

    this.noticationAgreements$ = this.store
      .select(
        configurationOptionSelectors.selectByKey,
        ConfigOptionsKeys.NOTIFICATIONS_AGREEMENTS_DISPLAY
      )
      .pipe(
        filter((x) => Boolean(x)),
        map((option) => JSON.parse(option.value))
      )
    this.showOffersAgreements$ = this.store
      .select(
        configurationOptionSelectors.selectByKey,
        ConfigOptionsKeys.NOTIFICATIONS_AGREEMENTS_DISPLAY_OFFERS
      )
      .pipe(
        filter((x) => Boolean(x)),
        map((option) => JSON.parse(option.value))
      )
    this.showNewslettersAgreements$ = this.store
      .select(
        configurationOptionSelectors.selectByKey,
        ConfigOptionsKeys.NOTIFICATIONS_AGREEMENTS_DISPLAY_NEWSLETTERS
      )
      .pipe(
        filter((x) => Boolean(x)),
        map((option) => JSON.parse(option.value))
      )

    this.optionValue$ = this.configOption$.pipe(
      filter((x) => Boolean(x)),
      map((option) =>
        (JSON.parse(option.value ?? '[]') as Array<{ inputName: string }>).reduce<
          MemberFormConfigValueMap
        >(
          (formData, inputGroup) => ({
            ...formData,
            [inputGroup.inputName]: inputGroup
          }),
          {}
        )
      )
    )

    this.valuesForm$ = includeMemberCategoryUrls
      ? combineLatest([this.optionValue$, this.configFormValues$]).pipe(
          untilDestroyed(this),
          map(([data, data2]) => {
            const dataMemberUrlProfile: MemberCategoryUrlValues = {}
            Object.keys(data2)
              .filter((key) => this.isMemberCategoryUrlKey(key))
              .forEach((selectedKey) => {
                dataMemberUrlProfile[selectedKey as MemberCategoryUrlKey] =
                  data2[selectedKey]
              })
            return { ...data, ...dataMemberUrlProfile }
          })
        )
      : this.optionValue$.pipe(untilDestroyed(this))

    this.form = this.buildMemberForm()
    this.form2 = this.fb.group({
      nl_member_categories_page_url: [null, []],
      en_member_categories_page_url: [null, []],
      fr_member_categories_page_url: [null, []],
      de_member_categories_page_url: [null, []]
    })

    this.regCategoryFilters$ = this.store
      .select(
        configurationOptionSelectors.selectByKey,
        ConfigOptionsKeys.REGISTRATION_CATEGORY_FILTER
      )
      .pipe(
        filter((x) => Boolean(x)),
        map((option) => JSON.parse(option.value))
      )

    this.regCategoryOptions$ = this.store
      .select(
        configurationOptionSelectors.selectByKey,
        ConfigOptionsKeys.REGISTRATION_CATEGORY_OPTIONS
      )
      .pipe(
        filter((x) => Boolean(x)),
        map((option) => JSON.parse(option.value))
      )
  }

  protected buildSerializedConfigOptionValue(formValue: MemberFormEditorValue): string {
    return JSON.stringify(
      Object.keys(formValue).map((key) => {
        const configValue = formValue[key as keyof MemberFormEditorValue]

        if (
          configValue &&
          typeof configValue === 'object' &&
          !configValue['isVisible'] &&
          !this.isMemberCategoryUrlKey(key)
        ) {
          configValue['isRequired'] = false
          configValue['isEditable'] = false
        }

        if (!this.isMemberCategoryUrlKey(key)) {
          if (!configValue || typeof configValue !== 'object') {
            return null
          }

          return {
            ...configValue,
            inputName: key
          }
        }
      })
    )
  }

  protected getMemberCategoryUrlValues(
    formValue: MemberFormEditorValue
  ): MemberCategoryUrlValues {
    const memberURLExplanations: MemberCategoryUrlValues = {}
    Object.keys(formValue).forEach((key) => {
      if (this.isMemberCategoryUrlKey(key)) {
        memberURLExplanations[key as MemberCategoryUrlKey] =
          formValue[key as keyof MemberFormEditorValue] as string | null
      }
    })
    return memberURLExplanations
  }

  protected dispatchCreateOrUpdateConfigOption(params: {
    configOption?: ConfigurationOption | null
    key: string
    value: string
    siteId?: number | null
  }) {
    const filteredValue = JSON.stringify(
      JSON.parse(params.value).filter((key) => key != null)
    )

    if (!params.configOption) {
      this.store.dispatch(
        configurationOptionActions.createOne({
          entity: {
            key: params.key,
            value: filteredValue,
            ...(params.siteId !== undefined ? { siteId: params.siteId } : {})
          }
        })
      )
      return
    }

    this.store.dispatch(
      configurationOptionActions.updateOneById({
        id: params.configOption.id,
        params: {
          value: filteredValue,
          ...(params.siteId !== undefined ? { siteId: params.siteId } : {})
        }
      })
    )
  }

  protected notifyOnSuccessfulUpsert(timeoutMs = 1000) {
    this.actions$
      .pipe(
        ofType(configurationOptionActions.upsertMany),
        take(1),
        timeout(timeoutMs),
        catchError(() => of(null))
      )
      .subscribe((action) =>
        action
          ? this.notificationService.emit('alerts.success', 'success')
          : null
      )
  }

  public onVisibleInputChange(formFieldName: string) {
    if (!this.form.value[formFieldName]?.isVisible) {
      this.form.controls[formFieldName]['controls']['isRequired'].patchValue(
        false
      )

      this.form.controls[formFieldName]['controls']['isEditable'].patchValue(
        false
      )
    }
  }

  public onFormSubmit(optionToSave: ConfigurationOptionSavePayload) {
    this.store
      .select(configurationOptionSelectors.selectByKey, optionToSave.key)
      .pipe(take(1))
      .subscribe((configOption) => {
        if (!configOption) {
          this.store.dispatch(
            configurationOptionActions.createOne({
              entity: {
                key: optionToSave.key,
                value: optionToSave.valueToSave
              }
            })
          )
        } else {
          this.store.dispatch(
            configurationOptionActions.updateOneById({
              id: configOption.id,
              params: {
                value: optionToSave.valueToSave
              }
            })
          )
        }
      })

    this.actions$
      .pipe(
        ofType(configurationOptionActions.upsertMany),
        take(1),
        timeout(5000),
        catchError(() => of(null))
      )
      .subscribe((action) =>
        action
          ? this.notificationService.emit('alerts.success', 'success')
          : this.notificationService.emit('alerts.error', 'error')
      )
  }

  protected buildMemberForm(): UntypedFormGroup {
    return this.fb.group({
      email: this.fb.group({
        title_en: ['Email', []],
        title_nl: ['Email', []],
        placeholder_nl: ['john@example.com', []],
        placeholder_en: ['john@example.com', []],
        title_de: ['Email', []],
        title_fr: ['Email', []],
        placeholder_fr: ['john@example.com', []],
        placeholder_de: ['john@example.com', []],
        isVisible: [true, []],
        isRequired: [true, []],
        isEditable: [true, []]
      }),
      password: this.fb.group({
        title_en: ['Password', []],
        title_nl: ['Wachtwoord', []],
        placeholder_nl: ['', []],
        placeholder_en: ['', []],
        title_de: ['Password', []],
        title_fr: ['Password', []],
        placeholder_fr: ['', []],
        placeholder_de: ['', []],
        isVisible: [true, []],
        isRequired: [true, []],
        isEditable: [true, []]
      }),
      passwordRepeat: this.fb.group({
        title_en: ['Repeat password', []],
        title_nl: ['Herhaal wachtwoord', []],
        placeholder_nl: ['', []],
        placeholder_en: ['', []],
        title_de: ['Repeat password', []],
        title_fr: ['Herhaal wachtwoord', []],
        placeholder_fr: ['', []],
        placeholder_de: ['', []],
        isVisible: [true, []],
        isRequired: [true, []],
        isEditable: [true, []]
      }),
      firstName: this.fb.group({
        title_en: ['First name', []],
        title_nl: ['Voornaam', []],
        placeholder_nl: ['First name', []],
        placeholder_en: ['First name', []],
        title_de: ['First name', []],
        title_fr: ['First name', []],
        placeholder_fr: ['', []],
        placeholder_de: ['', []],
        isVisible: [true, []],
        isRequired: [true, []],
        isEditable: [true, []]
      }),
      middleName: this.fb.group({
        title_en: ['Middle name', []],
        title_nl: ['Tussen voegsel', []],
        placeholder_nl: ['', []],
        placeholder_en: ['', []],
        title_de: ['Middle name', []],
        title_fr: ['Middle name', []],
        placeholder_fr: ['', []],
        placeholder_de: ['', []],
        isVisible: [true, []],
        isRequired: [true, []],
        isEditable: [true, []]
      }),
      lastName: this.fb.group({
        title_en: ['Last name', []],
        title_nl: ['Achternaam', []],
        placeholder_nl: ['', []],
        placeholder_en: ['', []],
        title_de: ['Last name', []],
        title_fr: ['Last name', []],
        placeholder_fr: ['', []],
        placeholder_de: ['', []],
        isVisible: [true, []],
        isRequired: [true, []],
        isEditable: [true, []]
      }),
      street: this.fb.group({
        title_en: ['Street', []],
        title_nl: ['Straat', []],
        placeholder_nl: ['', []],
        placeholder_en: ['', []],
        title_de: ['Street', []],
        title_fr: ['Street', []],
        placeholder_fr: ['', []],
        placeholder_de: ['', []],
        isVisible: [true, []],
        isRequired: [true, []],
        isEditable: [true, []]
      }),
      houseNr: this.fb.group({
        title_en: ['House number', []],
        title_nl: ['Huisnummer', []],
        placeholder_nl: ['', []],
        placeholder_en: ['', []],
        title_de: ['House number', []],
        title_fr: ['House number', []],
        placeholder_fr: ['', []],
        placeholder_de: ['', []],
        isVisible: [true, []],
        isRequired: [true, []],
        isEditable: [true, []]
      }),
      city: this.fb.group({
        title_en: ['City', []],
        title_nl: ['Stad', []],
        placeholder_nl: ['', []],
        placeholder_en: ['', []],
        title_de: ['City', []],
        title_fr: ['City', []],
        placeholder_fr: ['', []],
        placeholder_de: ['', []],
        isVisible: [true, []],
        isRequired: [true, []],
        isEditable: [true, []]
      }),
      municipality: this.fb.group({
        title_en: ['Municipality', []],
        title_nl: ['Deelgemeente', []],
        placeholder_nl: ['', []],
        placeholder_en: ['', []],
        title_de: ['Municipality', []],
        title_fr: ['Municipality', []],
        placeholder_fr: ['', []],
        placeholder_de: ['', []],
        isVisible: [true, []],
        isRequired: [true, []],
        isEditable: [true, []]
      }),
      region: this.fb.group({
        title_en: ['Region', []],
        title_nl: ['Gemeente', []],
        placeholder_nl: ['', []],
        placeholder_en: ['', []],
        title_de: ['Region', []],
        title_fr: ['Region', []],
        placeholder_fr: ['', []],
        placeholder_de: ['', []],
        isVisible: [true, []],
        isRequired: [true, []],
        isEditable: [true, []]
      }),
      country: this.fb.group({
        title_en: ['Country', []],
        title_nl: ['Land', []],
        placeholder_nl: ['', []],
        placeholder_en: ['', []],
        title_de: ['Country', []],
        title_fr: ['Country', []],
        placeholder_fr: ['', []],
        placeholder_de: ['', []],
        isVisible: [true, []],
        isRequired: [true, []],
        isEditable: [true, []]
      }),
      zipcode: this.fb.group({
        title_en: ['Zipcode', []],
        title_nl: ['Postcode', []],
        placeholder_nl: ['', []],
        placeholder_en: ['', []],
        title_de: ['', []],
        title_fr: ['', []],
        placeholder_fr: ['', []],
        placeholder_de: ['', []],
        isVisible: [true, []],
        isRequired: [true, []],
        isEditable: [true, []]
      }),
      postbox: this.fb.group({
        title_en: ['Postbox', []],
        title_nl: ['Postbus', []],
        placeholder_nl: ['', []],
        placeholder_en: ['', []],
        title_de: ['', []],
        title_fr: ['', []],
        placeholder_fr: ['', []],
        placeholder_de: ['', []],
        isVisible: [true, []],
        isRequired: [true, []],
        isEditable: [true, []]
      }),
      language: this.fb.group({
        title_en: ['Language', []],
        title_nl: ['Taal', []],
        placeholder_nl: ['', []],
        placeholder_en: ['', []],
        title_de: ['', []],
        title_fr: ['', []],
        placeholder_fr: ['', []],
        placeholder_de: ['', []],
        isVisible: [true, []],
        isRequired: [true, []],
        isEditable: [true, []]
      }),
      phone: this.fb.group({
        title_en: ['Phone', []],
        title_nl: ['Telefoon', []],
        placeholder_nl: ['', []],
        placeholder_en: ['', []],
        title_de: ['', []],
        title_fr: ['', []],
        placeholder_fr: ['', []],
        placeholder_de: ['', []],
        isVisible: [true, []],
        isRequired: [true, []],
        isEditable: [true, []]
      }),
      mobile: this.fb.group({
        title_en: ['Mobile number', []],
        title_nl: ['Mobiel', []],
        placeholder_nl: ['', []],
        placeholder_en: ['', []],
        title_de: ['', []],
        title_fr: ['', []],
        placeholder_fr: ['', []],
        placeholder_de: ['', []],
        isVisible: [true, []],
        isRequired: [true, []],
        isEditable: [true, []]
      }),
      gender: this.fb.group({
        title_en: ['Gender', []],
        title_nl: ['Geslacht', []],
        placeholder_nl: ['', []],
        placeholder_en: ['', []],
        title_de: ['', []],
        title_fr: ['', []],
        placeholder_fr: ['', []],
        placeholder_de: ['', []],
        isVisible: [true, []],
        isRequired: [true, []],
        isEditable: [true, []]
      }),
      category: this.fb.group({
        title_en: ['Category', []],
        title_nl: ['Categorie', []],
        placeholder_nl: ['', []],
        placeholder_en: ['', []],
        title_de: ['', []],
        title_fr: ['', []],
        placeholder_fr: ['', []],
        placeholder_de: ['', []],
        isVisible: [true, []],
        isRequired: [true, []],
        isEditable: [true, []]
      }),
      birthDate: this.fb.group({
        title_en: ['Birthdate', []],
        title_nl: ['Geboortedatum', []],
        placeholder_nl: ['', []],
        placeholder_en: ['', []],
        title_de: ['', []],
        title_fr: ['', []],
        placeholder_fr: ['', []],
        placeholder_de: ['', []],
        isVisible: [true, []],
        isRequired: [true, []],
        isEditable: [true, []]
      }),
      shouldSync: this.fb.group({
        title_en: ['Should synchronize', []],
        title_nl: ['Synchronisatie', []],
        placeholder_nl: ['', []],
        placeholder_en: ['', []],
        title_de: ['', []],
        title_fr: ['', []],
        placeholder_fr: ['', []],
        placeholder_de: ['', []],
        isVisible: [false, []],
        isRequired: [false, []],
        isEditable: [false, []]
      }),
      memberNumber: this.fb.group({
        title_en: ['Member/student/employee number', []],
        title_nl: ['Abonnee/student/werknemer nummer', []],
        placeholder_nl: ['', []],
        placeholder_en: ['', []],
        title_de: ['', []],
        title_fr: ['', []],
        placeholder_fr: ['', []],
        placeholder_de: ['', []],
        isVisible: [true, []],
        isRequired: [true, []],
        isEditable: [true, []]
      }),
      generalTerms: this.fb.group({
        title_en: ['General terms', []],
        title_nl: ['Algemene voorwaarden', []],
        placeholder_nl: ['', []],
        placeholder_en: ['', []],
        title_de: ['', []],
        title_fr: ['', []],
        placeholder_fr: ['', []],
        placeholder_de: ['', []],
        isVisible: [true, []],
        isRequired: [true, []],
        isEditable: [true, []]
      }),
      privacyTerms: this.fb.group({
        title_en: ['Privacy terms', []],
        title_nl: ['Privacy voorwaarden', []],
        placeholder_nl: ['', []],
        placeholder_en: ['', []],
        title_de: ['', []],
        title_fr: ['', []],
        placeholder_fr: ['', []],
        placeholder_de: ['', []],
        isVisible: [true, []],
        isRequired: [true, []],
        isEditable: [true, []]
      }),
      biometricTerms: this.fb.group({
        title_en: ['Biometric terms', []],
        title_nl: ['Biometrische voorwaarden', []],
        placeholder_nl: ['', []],
        placeholder_en: ['', []],
        title_de: ['', []],
        title_fr: ['', []],
        placeholder_fr: ['', []],
        placeholder_de: ['', []],
        isVisible: [true, []],
        isRequired: [true, []],
        isEditable: [true, []]
      }),
      organizationId: this.fb.group({
        title_en: ['Organization id', []],
        title_nl: ['Organisatie id', []],
        placeholder_nl: ['', []],
        placeholder_en: ['', []],
        title_de: ['', []],
        title_fr: ['', []],
        placeholder_fr: ['', []],
        placeholder_de: ['', []],
        isVisible: [true, []],
        isRequired: [true, []],
        isEditable: [true, []]
      }),
      documents: this.fb.group({
        title_en: ['Upload documents', []],
        title_nl: ['Upload documents (NL)', []],
        placeholder_nl: ['', []],
        placeholder_en: ['', []],
        title_de: ['', []],
        title_fr: ['', []],
        placeholder_fr: ['', []],
        placeholder_de: ['', []],
        isVisible: [true, []],
        isRequired: [false, []],
        isEditable: [true, []]
      }),
      profilePicture: this.fb.group({
        title_en: ['Upload profile picture', []],
        title_nl: ['Upload profile picture (NL)', []],
        placeholder_nl: ['', []],
        placeholder_en: ['', []],
        title_de: ['', []],
        title_fr: ['', []],
        placeholder_fr: ['', []],
        placeholder_de: ['', []],
        isVisible: [true, []],
        isRequired: [false, []],
        isEditable: [true, []]
      }),
      status: this.fb.group({
        title_en: ['Status', []],
        title_nl: ['Status', []],
        placeholder_nl: ['', []],
        placeholder_en: ['', []],
        title_de: ['', []],
        title_fr: ['', []],
        placeholder_fr: ['', []],
        placeholder_de: ['', []],
        isVisible: [true, []],
        isRequired: [false, []],
        isEditable: [true, []]
      })
    })
  }
}
